import { CONFIG_ELEMENTS, type ButtonElementValue } from '@vedalib/editor/lib/elements'
import { useCallback } from 'react'

import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlockId, getIsInteractiveMode } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { testPropsEl } from 'utils/test/qaData'

import { TooltipCourse } from '../../courseKit/DropDownCourse'
import type { Properties } from '../../elements.types'
import type { ButtonElementType } from './buttonTypes'
import BaseButtonElementStyled from './styled/BaseButtonElementStyled'

interface IBaseButtonElementProps
  extends Pick<Properties<ButtonElementType>, 'font' | 'styles' | 'mode'> {
  label?: string
  disabled?: boolean
  onClick?: () => void
  icon?: React.ReactElement | null
  iconPosition?: 'left' | 'right'
  action?: ButtonElementValue['action']
  tooltip?: string
  elementId?: string
  children?: React.ReactNode
}

const BaseButtonElement: React.FC<IBaseButtonElementProps> = ({
  label,
  disabled: disabledProp,
  styles,
  onClick,
  icon,
  iconPosition,
  action,
  tooltip,
  font,
  mode,
  elementId = '',
  children,
}) => {
  const blockUuid = useProjectContext(getBlockId) || ''
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: blockUuid,
      elementType: CONFIG_ELEMENTS.button.type,
      elementId,
    },
  )
  const isInteractive = useProjectContext(getIsInteractiveMode)
  const disabled = !isInteractive ? false : disabledProp

  const onClickHandler = useCallback(() => {
    if (disabled || !isInteractive || mode.editorMode === AppMode.comment) {
      return
    }
    if (elementId) {
      const data = entityAnalyticsConfig.interacted({
        resultExtensions: { label, action },
        objectExtensions: baseObjectExtensions,
        elementId,
      })
      sendXapiData(data)
    }
    onClick?.()
  }, [
    action,
    entityAnalyticsConfig,
    disabled,
    elementId,
    isInteractive,
    label,
    mode.editorMode,
    onClick,
    sendXapiData,
    baseObjectExtensions,
  ])

  return (
    //TODO: think about using tooltip
    <TooltipCourse overlay={tooltip}>
      <BaseButtonElementStyled
        {...testPropsEl('courseButton', { value: action }, { label, disabled })}
        $disabled={disabled}
        $fonts={font}
        $iconPosition={iconPosition}
        $styles={styles}
        aria-label={label}
        disabled={disabled}
        onClick={onClickHandler}
      >
        {children ? (
          children
        ) : (
          <>
            {label}
            {icon && iconPosition && icon}
          </>
        )}
      </BaseButtonElementStyled>
    </TooltipCourse>
  )
}

export default BaseButtonElement
